import { ReactNode } from 'react';
import { useAuthStore } from '@/stores/auth';
import { hasAnyPermission, hasAllPermissions, type UserPermission } from '@/types/auth';

interface CanViewProps {
  /** The content to conditionally render */
  children: ReactNode;
  /** Required permission(s) to view the content */
  permission: UserPermission | UserPermission[];
  /** If true, user needs ANY of the permissions. If false, user needs ALL of them */
  requireAny?: boolean;
  /** Optional fallback content to render if user doesn't have permission */
  fallback?: ReactNode;
}

/**
 * CanView component to conditionally render content based on permissions
 *
 * @example
 * ```tsx
 * // Show button only if user has permission
 * <CanView permission="incidents:create">
 *   <button>Create Incident</button>
 * </CanView>
 *
 * // Show content if user has ANY of the permissions
 * <CanView
 *   permission={['incidents:update', 'incidents:delete']}
 *   requireAny={true}
 * >
 *   <IncidentActions />
 * </CanView>
 *
 * // Show content if user has ALL permissions
 * <CanView
 *   permission={['users:read', 'users:update']}
 * >
 *   <UserEditForm />
 * </CanView>
 *
 * // Show fallback if permission is missing
 * <CanView
 *   permission="analytics:advanced"
 *   fallback={<p>Upgrade to access advanced analytics</p>}
 * >
 *   <AdvancedAnalytics />
 * </CanView>
 * ```
 */
export function CanView({
  children,
  permission,
  requireAny = false,
  fallback = null,
}: CanViewProps) {
  const user = useAuthStore((state) => state.user);

  if (!user) {
    return <>{fallback}</>;
  }

  const permissions = Array.isArray(permission) ? permission : [permission];

  // Route through wildcard-aware helpers so `['*']` holders resolve correctly (L-026 consistency).
  const allowed = requireAny
    ? hasAnyPermission(user.permissions, permissions)
    : hasAllPermissions(user.permissions, permissions);

  if (!allowed) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
